import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

export default function CartDrawer() {
  const { detailed, updateQty, removeItem, subtotal, shipping, total, open, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-[#F7F3EE] border-black/10 w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-black/10">
          <SheetTitle className="font-serif-brand text-2xl font-light text-[#111111]">Your Cart</SheetTitle>
        </SheetHeader>

        {detailed.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-black/40" data-testid="cart-empty">
            <ShoppingBag size={32} strokeWidth={1} />
            <p className="text-sm tracking-wide">Your cart is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="cart-items">
              {detailed.map((i) => (
                <div key={`${i.slug}-${i.variant}`} data-testid={`cart-item-${i.slug}-${i.variant}`} className="flex gap-4 border-b border-black/5 pb-5">
                  <img src={i.image} alt={i.name} className="w-16 h-20 object-cover bg-[#EFE9E1]" />
                  <div className="flex-1">
                    <p className="font-serif-brand text-lg leading-tight">{i.name}</p>
                    <p className="text-[0.62rem] uppercase tracking-[0.2em] text-[#BA8D53] mt-0.5">{i.variantLabel}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-black/15">
                        <button data-testid={`cart-qty-minus-${i.slug}-${i.variant}`} className="px-2 py-1 hover:bg-black/5" onClick={() => updateQty(i.slug, i.variant, i.qty - 1)}><Minus size={12} /></button>
                        <span data-testid={`cart-qty-${i.slug}-${i.variant}`} className="px-3 text-sm">{i.qty}</span>
                        <button data-testid={`cart-qty-plus-${i.slug}-${i.variant}`} className="px-2 py-1 hover:bg-black/5" onClick={() => updateQty(i.slug, i.variant, i.qty + 1)}><Plus size={12} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{inr(i.lineTotal)}</span>
                        <button data-testid={`cart-remove-${i.slug}-${i.variant}`} onClick={() => removeItem(i.slug, i.variant)} className="text-black/40 hover:text-black"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-black/10 px-6 py-5 space-y-2 text-sm">
              <div className="flex justify-between text-black/60"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(subtotal)}</span></div>
              <div className="flex justify-between text-black/60">
                <span>Shipping</span>
                <span data-testid="cart-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span>
              </div>
              {subtotal > 0 && subtotal < 999 && (
                <p className="text-[0.65rem] text-[#BA8D53] tracking-wide">Add {inr(999 - subtotal)} more for free shipping</p>
              )}
              <div className="flex justify-between font-medium text-base pt-2 border-t border-black/10"><span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
              <button
                data-testid="cart-checkout-button"
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="btn-ink w-full justify-center mt-3"
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
